import React, { useState, useEffect } from 'react';
import useSWR from 'swr';
import { Input } from './ui/input';
import { Label } from './ui/label';
import ActionButton from './ui/action-button';
import ServiceToggle from './ServiceToggle';
import { Upload, Eye, CheckCircle, Palette, Image, Building2, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';
import apiV2 from '../utils/apiV2';
import TabPageLayout, { PageLoader } from './TabPageLayout';
import FormSection from './FormSection';

// SWR fetcher using apiV2
const fetcher = async (url) => {
  const response = await apiV2.get(url);
  return response.data;
};

// Color presets for quick selection
const COLOR_PRESETS = [
  { name: 'Blue', value: '#2563EB' },
  { name: 'Green', value: '#10B981' },
  { name: 'Purple', value: '#7C3AED' },
  { name: 'Red', value: '#DC2626' },
  { name: 'Orange', value: '#EA580C' },
  { name: 'Pink', value: '#DB2777' },
  { name: 'Teal', value: '#0D9488' },
  { name: 'Indigo', value: '#4F46E5' },
];

const BrandingTab = () => {
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  
  // Form state
  const [logoUrl, setLogoUrl] = useState('');
  const [primaryColor, setPrimaryColor] = useState('#2563EB');
  const [showPoweredBy, setShowPoweredBy] = useState(true); // true = "Powered by BookingFlows", false = org name only
  const [companyWebsite, setCompanyWebsite] = useState('');

  // Fetch organization settings with SWR using v2 API
  const { data: orgData, isLoading, mutate } = useSWR(
    '/auth/organizations/me',
    fetcher,
    { revalidateOnFocus: true, dedupingInterval: 30000 } // Branding rarely changes
  );

  // Update local state when org data is fetched
  useEffect(() => {
    if (orgData) {
      setLogoUrl(orgData.logoUrl || '');
      setPrimaryColor(orgData.primaryColor || '#2563EB');
      setShowPoweredBy(orgData.showPoweredByBookingFlows !== false); // Default to true
      setCompanyWebsite(orgData.companyWebsite || '');
    }
  }, [orgData]);

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Please upload an image file');
      return;
    }

    // Validate file size (max 2MB - matches backend)
    if (file.size > 2 * 1024 * 1024) {
      toast.error('Logo must be less than 2MB');
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await apiV2.post('/auth/organizations/me/upload-logo', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      const uploadedLogoUrl = response.data.logoUrl;
      setLogoUrl(uploadedLogoUrl);
      toast.success('Logo uploaded successfully!');
      mutate(); // Refresh org data
    } catch (error) {
      console.error('Error uploading logo:', error);
      toast.error('Failed to upload logo');
    } finally {
      setUploading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await apiV2.patch('/auth/organizations/me', {
        logoUrl: logoUrl,
        primaryColor: primaryColor,
        showPoweredByBookingFlows: showPoweredBy,
        companyWebsite: companyWebsite
      });

      toast.success('Branding saved! Changes will appear on customer-facing pages.');
      mutate(); // Refresh org data
      
      // Notify setup guide of data change
      window.dispatchEvent(new Event('setupDataChanged'));
    } catch (error) {
      console.error('Error saving branding:', error);
      toast.error('Failed to save branding');
    } finally {
      setSaving(false);
    }
  };

  const removeLogo = async () => {
    try {
      await apiV2.patch('/auth/organizations/me', { logoUrl: '' });
      setLogoUrl('');
      toast.success('Logo removed');
      mutate();
    } catch (error) {
      toast.error('Failed to remove logo');
    }
  };

  if (isLoading) {
    return <PageLoader />;
  }

  return (
    <TabPageLayout
      title="Branding"
      subtitle="Customize your logo, colors, and white-label settings for customer-facing pages"
      actions={
        <ActionButton
          onClick={handleSave}
          loading={saving}
          disabled={saving}
          icon={CheckCircle}
        >
          Save Changes
        </ActionButton>
      }
    >
      <div className="space-y-6">
        {/* STEP 1: Logo Upload */}
        <FormSection
          stepNumber={1}
          title="Your Logo"
          description="Appears on booking widget, customer tickets, waivers, and emails"
          color="blue"
        >
          <div className="space-y-4">
            {/* Current Logo Preview */}
            {logoUrl ? (
              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border border-gray-200">
                <div className="flex items-center gap-4">
                  <img 
                    src={logoUrl} 
                    alt="Your logo" 
                    className="h-14 w-auto max-w-[200px] object-contain"
                  />
                  <span className="text-sm text-green-600 font-medium">✓ Logo uploaded</span>
                </div>
                <button
                  onClick={removeLogo}
                  className="text-sm text-red-600 hover:text-red-700 hover:underline"
                >
                  Remove
                </button>
              </div>
            ) : (
              <div className="flex items-center gap-3 p-4 bg-gray-50 rounded-lg border border-dashed border-gray-300">
                <Image className="w-8 h-8 text-gray-400" />
                <span className="text-sm text-gray-500">No logo uploaded yet</span>
              </div>
            )}
            
            {/* Upload Button */}
            <div className="flex items-center gap-3">
              <input
                type="file"
                accept="image/png,image/jpeg,image/webp"
                onChange={handleLogoUpload}
                className="hidden"
                id="logo-upload"
                disabled={uploading}
              />
              <label 
                htmlFor="logo-upload"
                className={`inline-flex items-center gap-2 px-4 py-2.5 border border-gray-200 rounded-lg text-sm font-medium transition-all cursor-pointer
                  ${uploading ? 'bg-gray-100 text-gray-400' : 'bg-white text-gray-700 hover:bg-gray-50'}`}
              >
                <Upload className="w-4 h-4" />
                {uploading ? 'Uploading...' : logoUrl ? 'Change Logo' : 'Upload Logo'}
              </label>
              <span className="text-xs text-gray-500">PNG, JPG, WebP • Max 2MB</span>
            </div>
          </div>
        </FormSection>

        {/* STEP 2: Brand Color */}
        <FormSection
          stepNumber={2}
          title="Brand Color"
          description="Primary color for buttons, accents, and highlights on customer pages"
          color="purple"
        >
          <div className="space-y-4">
            {/* Color Picker Row */}
            <div className="flex items-center gap-4">
              <div className="relative">
                <Input
                  type="color"
                  value={primaryColor}
                  onChange={(e) => setPrimaryColor(e.target.value)}
                  className="h-12 w-14 cursor-pointer p-1 rounded-lg"
                />
              </div>
              <Input
                type="text"
                value={primaryColor}
                onChange={(e) => {
                  const val = e.target.value;
                  if (/^#[0-9A-Fa-f]{0,6}$/.test(val)) {
                    setPrimaryColor(val);
                  }
                }}
                placeholder="#2563EB"
                className="font-mono w-32 text-base"
                maxLength={7}
              />
              <div 
                className="h-10 px-4 rounded-lg flex items-center text-white text-sm font-medium shadow-sm"
                style={{ backgroundColor: primaryColor }}
              >
                Button Preview
              </div>
            </div>
            
            {/* Color Presets */}
            <div>
              <Label className="text-sm text-gray-600 mb-2 block">Quick presets</Label>
              <div className="flex flex-wrap gap-2">
                {COLOR_PRESETS.map((preset) => (
                  <button
                    key={preset.value}
                    onClick={() => setPrimaryColor(preset.value)}
                    className={`w-8 h-8 rounded-full border-2 transition-all hover:scale-110 ${
                      primaryColor === preset.value ? 'border-gray-900 ring-2 ring-offset-2 ring-gray-400' : 'border-gray-200'
                    }`}
                    style={{ backgroundColor: preset.value }}
                    title={preset.name}
                  />
                ))}
              </div>
            </div>
          </div>
        </FormSection>

        {/* STEP 3: White Label Settings */}
        <FormSection
          stepNumber={3}
          title="White Label"
          description="Control branding on customer-facing pages"
          color="green"
        >
          <div className="space-y-4">
            {/* Toggle */}
            <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border border-gray-200">
              <div className="flex-1">
                <Label className="text-sm font-medium">Enable White Label</Label>
                <p className="text-xs text-gray-500 mt-1">
                  {showPoweredBy 
                    ? 'Default: "Powered by BookingFlows"'
                    : `White-labeled: "© ${orgData?.name || 'Your Company'}"`
                  }
                </p>
              </div>
              <ServiceToggle
                checked={!showPoweredBy}
                onChange={(val) => setShowPoweredBy(!val)}
              />
            </div>
            
            {/* Company Website - Only shown when white-labeled */}
            {!showPoweredBy && (
              <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                <Label className="text-sm font-medium mb-2 block">
                  Your Website URL
                </Label>
                <Input
                  type="url"
                  value={companyWebsite}
                  onChange={(e) => setCompanyWebsite(e.target.value)}
                  placeholder="https://yourcompany.com"
                  className="bg-white"
                />
                <p className="text-xs text-gray-500 mt-2">
                  Link destination when customers click your copyright
                </p>
              </div>
            )}
            
            {/* Preview */}
            <div className="p-3 bg-white rounded-lg border border-gray-200">
              <p className="text-xs text-gray-400 mb-2">Preview:</p>
              <div className="flex items-center justify-center py-2">
                <span className="text-xs text-gray-400">
                  {showPoweredBy ? (
                    <>Powered by <span className="font-semibold" style={{ color: primaryColor }}>BookingFlows</span></>
                  ) : (
                    <>© {orgData?.name || 'Your Company'}</>
                  )}
                </span>
              </div>
            </div>
            
            {/* Where branding appears */}
            <div className="p-3 bg-blue-50 rounded-lg border border-blue-100">
              <p className="text-xs font-medium text-blue-900 mb-1">Your branding appears on:</p>
              <p className="text-xs text-blue-700">
                Booking widget, customer tickets, waivers, confirmation emails, reminder emails, check-in kiosk
              </p>
            </div>
          </div>
        </FormSection>
      </div>
    </TabPageLayout>
  );
};

export default BrandingTab;
